import React, { useState, useCallback, useMemo } from 'react';
import { CertificateCodeEditor } from './CertificateCodeEditor';
import { CertificatePreview } from './CertificatePreview';
import { useCertificatePreview } from './useCertificatePreview';

interface CertificateEditorLayoutProps {
  htmlContent: string;
  onChange: (value: string) => void;
  onSave: () => void;
  isSaving?: boolean;
}

export function CertificateEditorLayout({
  htmlContent,
  onChange,
  onSave,
  isSaving = false
}: CertificateEditorLayoutProps) {
  const [isChangeDetected, setIsChangeDetected] = useState(false);
  const changeDetectionTimerRef = React.useRef<number | null>(null);

  const { renderedHtml, isLoading, handleIframeLoad } = useCertificatePreview({
    htmlContent,
    isChangeDetected
  });

  const handleCodeChange = useCallback((value: string) => {
    setIsChangeDetected(true);
    onChange(value);
    
    if (changeDetectionTimerRef.current !== null) {
      clearTimeout(changeDetectionTimerRef.current);
    }
    
    changeDetectionTimerRef.current = window.setTimeout(() => {
      setIsChangeDetected(false);
      changeDetectionTimerRef.current = null;
    }, 500);
  }, [onChange]);

  React.useEffect(() => {
    return () => {
      if (changeDetectionTimerRef.current !== null) {
        clearTimeout(changeDetectionTimerRef.current);
      }
    };
  }, []);

  const codeEditor = useMemo(() => (
    <CertificateCodeEditor 
      htmlContent={htmlContent} 
      onChange={handleCodeChange}
      onSave={onSave}
      isSaving={isSaving}
    />
  ), [htmlContent, handleCodeChange, onSave, isSaving]);

  const previewPanel = useMemo(() => (
    <CertificatePreview 
      renderedHtml={renderedHtml}
      isLoading={isLoading}
      onIframeLoad={handleIframeLoad}
    />
  ), [renderedHtml, isLoading, handleIframeLoad]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 h-full min-h-[600px]">
      <div className="relative h-full">
        {codeEditor}
      </div>
      
      <div className="h-full">
        {previewPanel}
      </div>
    </div>
  );
}
